/**
 * The ONE file that imports `@dotpe/rista-library`.
 *
 * Every receipt, KOT and label is laid out by the library's templates — this package never lays
 * out a line itself (charter constraint 2). Imported as modules, never read off `window.RL` /
 * `globalThis.bill`, which is how v1 reached the same code on web and mobile respectively
 * (CLAUDE.md, audit check K). Each module self-initialises on first call.
 *
 * The `any` from the untyped library stops here: callers get typed functions.
 */
import billImport from "@dotpe/rista-library/lib/bill/bill.js";
import salesSummaryImport from "@dotpe/rista-library/lib/reports/salesummaryreport.js";

const bill = billImport as any;
const salesummaryreport = salesSummaryImport as any;

/** v1 `MULTI_LANGUAGE_TEMPLATE_NAME` — the one template the library renders as HTML. */
export const MULTI_LANGUAGE_TEMPLATE_NAME = "Receipt - Multi Language";

/** Pages of an HTML render are separated by this marker (split bills). */
export const HTML_PAGE_BREAK = "<!--SPLIT_BREAK-->";

/** v1 printHelper :192 — the only way a bill or ticket takes the HTML path. */
export function isHtmlTemplate(params: any): boolean {
  return params?.billTemplate?.templateName === MULTI_LANGUAGE_TEMPLATE_NAME;
}

/** A bill or a ticket, as token text — or as HTML when `isHtmlTemplate(params)`. */
export function renderBillContent(data: any, params: any): string {
  return bill.getPrintContent(data, params);
}

/* ------------------------------------------------------- the documents added in S3 */

/**
 * v1 `printItemLabelTemplate` — item labels (ZPL, not ESC/POS). The library decides WHICH label
 * printer each item's label belongs on and answers a map of `printerId → content`, so routing for
 * this one document comes out of the library rather than out of `routing.ts`.
 *
 * The input is cloned because the library mutates it (it reorders `itemLabelTemplate` so a barcode
 * is always last) — v1 web passed a `structuredClone`, v1 mobile passed the live object.
 */
export function renderItemLabelContent(sale: any, itemLabelSettings: any, printInfo?: any): Record<string, string> {
  const ctx: any = { sale, itemLabelSettings };
  if (printInfo) ctx.printInfo = printInfo;
  return bill.getItemLabelPrintContent(ctx) || {};
}

/**
 * 🔴 The library's two REPORT modules assign an UNDECLARED `isApp` when they are loaded as
 * CommonJS (`lib/reports/salesummaryreport.js:16`, `settlementreport.js:16`) — a strict-mode
 * ReferenceError, thrown on EVERY call, because `init()` runs on each one. `bill.js` declares its
 * own and is safe; only these two are not. Reproduced in plain node, under babel's output, and in
 * apps/web's jest (CRA's dependency transform, the one webpack uses).
 *
 * Web v1 never meets it: it reads `window.RL.salesummaryreport` from the browser bundle, where
 * `typeof exports === "undefined"` and that branch is never taken. Mobile v1 imports the module
 * (`src/utils/global.js`) and so should hit it — recorded in docs/flows/PRINTING.md §6.
 *
 * Declaring the binding the library means to own is the smallest thing that keeps the module
 * IMPORT (CLAUDE.md's rule K) working. It is a write of a variable nothing else reads — these two
 * modules never read `isApp` — never a read of host state, and it is undone by the library
 * declaring `var isApp` in both files, which is the real fix.
 */
function withReportsGlobal<T>(call: () => T): T {
  // written as DIRECT member access on globalThis, twice: an alias (`const g = globalThis`) is what
  // audit check L refuses, and rightly — it is how a host read hides from it
  if ((globalThis as { isApp?: boolean }).isApp === undefined) (globalThis as { isApp?: boolean }).isApp = false;
  return call();
}

/** v1 `printSalesummary` — the cashier's end-of-shift summary, as token text. */
export function renderSalesSummaryContent(summary: any, maxChars: number | string): string {
  return withReportsGlobal(() => salesummaryreport.getPrintContent(summary, { maxChars }));
}

/** v1 `nativeHooks.printbill` — the receipt a printer's setup screen prints to prove it works. */
export function renderTestPrintContent(printer: any): string {
  // v1 passes `maxChars: ""`, which the template prints as the line-width row and the library's
  // alignContent reads as its `|| 46` default. Kept: the receipt is byte-for-byte v1's.
  return bill.getTestPrintContent({
    maxChars: "",
    label: printer?.label,
    ip: printer?.ip,
    port: printer?.port,
    contentType: printer?.contentType,
  });
}

/** v1 `nativeHooks.printbill`, the label-printer branch — ZPL, and no `contentType`. */
export function renderTestPrintLabelContent(printer: any): string {
  return bill.getTestPrintLabelContent({
    label: printer?.label,
    ip: printer?.ip,
    port: printer?.port,
    printWidth: printer?.printWidth,
  });
}
